// Claves de sesión no exportables (ADR 0004 §2) y mensajes firmados con contexto.
import { contextMessage, toHex } from './crypto.js';
import type { CaptureKeyAlg, CaptureShot, CaptureVideoKind } from './types.js';

/** Contextos de firma (ADR 0004 §2). */
export const CAPTURE_CONTEXTS = {
  request: 'kyp.capture.request.v1',
  shot: 'kyp.capture.shot.v1',
  video: 'kyp.capture.video.v1',
  bundle: 'kyp.capture.bundle.v1',
} as const;

/** Lo que firma cada petición (§5). `method` en mayúsculas, `timestamp` en ms. */
export interface RequestToSign {
  session_id: string;
  nonce: string;
  timestamp: number;
  method: string;
  path: string;
  body_sha256: string;
}

/** Mensaje `kyp.capture.request.v1`. */
export function requestMessage(r: RequestToSign): Uint8Array {
  return contextMessage(CAPTURE_CONTEXTS.request, {
    session_id: r.session_id,
    nonce: r.nonce,
    timestamp: r.timestamp,
    method: r.method.toUpperCase(),
    path: r.path,
    body_sha256: r.body_sha256,
  });
}

/** Mensaje `kyp.capture.shot.v1` de un disparo (sin su firma). */
export function shotMessage(
  sessionId: string,
  serverNonce: string,
  shot: Omit<CaptureShot, 'signature'>,
): Uint8Array {
  return contextMessage(CAPTURE_CONTEXTS.shot, {
    session_id: sessionId,
    server_nonce: serverNonce,
    index: shot.index,
    chosen_frame: shot.chosen_frame,
    chain_root: shot.chain_root,
    frames: shot.frames,
  });
}

/** Mensaje `kyp.capture.video.v1` de una firma periódica o final. */
export function videoMessage(c: {
  session_id: string;
  server_nonce: string;
  kind: CaptureVideoKind;
  upto: number;
  hash: string;
}): Uint8Array {
  return contextMessage(CAPTURE_CONTEXTS.video, {
    session_id: c.session_id,
    server_nonce: c.server_nonce,
    kind: c.kind,
    upto: c.upto,
    hash: c.hash,
  });
}

/** Mensaje `kyp.capture.bundle.v1`: el bundle completo sin `signature`. */
export function bundleMessage(bundle: object): Uint8Array {
  const rest: Record<string, unknown> = { ...bundle };
  delete rest.signature;
  return contextMessage(CAPTURE_CONTEXTS.bundle, rest);
}

/** Cualquier cosa capaz de firmar mensajes de captura (las claves reales o un doble de pruebas). */
export interface CaptureSigner {
  readonly alg: CaptureKeyAlg;
  publicKeyHex(): string;
  sign(message: Uint8Array): Promise<string>;
}

export interface SessionKeysOptions {
  /** Fuerza un algoritmo (por defecto Ed25519 y, si no hay, ES256). */
  prefer?: CaptureKeyAlg;
  /** WebCrypto a usar (por defecto `globalThis.crypto.subtle`). */
  subtle?: SubtleCrypto;
}

/**
 * Par de claves de sesión generado con WebCrypto y **no exportable** (`extractable: false`).
 * Ed25519 si el navegador lo soporta; si no, ECDSA P-256 + SHA-256 (ES256, firma `r‖s`).
 *
 * ```ts
 * const keys = await SessionKeys.generate();
 * keys.alg; keys.publicKeyHex(); await keys.sign(bytes); // hex de 64 bytes
 * ```
 */
export class SessionKeys implements CaptureSigner {
  private constructor(
    readonly alg: CaptureKeyAlg,
    private readonly privateKey: CryptoKey,
    private readonly publicHex: string,
    private readonly subtle: SubtleCrypto,
  ) {}

  /** Genera el par (Ed25519 preferido). */
  static async generate(opts: SessionKeysOptions = {}): Promise<SessionKeys> {
    const subtle = opts.subtle ?? globalThis.crypto.subtle;
    if (opts.prefer !== 'ES256') {
      try {
        const pair = (await subtle.generateKey({ name: 'Ed25519' }, false, [
          'sign',
          'verify',
        ])) as CryptoKeyPair;
        const raw = new Uint8Array(await subtle.exportKey('raw', pair.publicKey));
        return new SessionKeys('Ed25519', pair.privateKey, toHex(raw), subtle);
      } catch (err) {
        if (opts.prefer === 'Ed25519') throw err;
      }
    }
    const pair = await subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, [
      'sign',
      'verify',
    ]);
    const raw = new Uint8Array(await subtle.exportKey('raw', pair.publicKey));
    return new SessionKeys('ES256', pair.privateKey, toHex(raw), subtle);
  }

  /** Clave pública `raw` en hex (Ed25519: 32 bytes; ES256: 65 bytes `04‖X‖Y`). */
  publicKeyHex(): string {
    return this.publicHex;
  }

  /** ¿La clave privada es exportable? Siempre `false`: se comprueba en las pruebas. */
  get exportable(): boolean {
    return this.privateKey.extractable;
  }

  /** Firma unos bytes y devuelve la firma en hex (64 bytes). */
  async sign(message: Uint8Array): Promise<string> {
    const algorithm =
      this.alg === 'Ed25519' ? { name: 'Ed25519' } : { name: 'ECDSA', hash: 'SHA-256' };
    const sig = await this.subtle.sign(algorithm, this.privateKey, new Uint8Array(message));
    return toHex(new Uint8Array(sig));
  }
}
